// Pure motion engine for the cardiograph trace (TASK-2494).
//
// Generates the next sample (Y in viewBox coordinates) for a rolling
// SVG path based on the current state. No DOM, no React — testable
// in isolation. The component owns the rolling buffer + RAF loop;
// this file owns the *shape* of the heartbeat per state.
//
// ViewBox: 0..VIEW_HEIGHT (Y). Trace centred at MID_Y.

import type { CardiographState, DockerOperation, PhaseName } from './types'

export const VIEW_WIDTH = 800
export const VIEW_HEIGHT = 120
export const MID_Y = VIEW_HEIGHT / 2

interface NextSampleArgs {
  state: CardiographState
  /** Monotonic tick counter (incremented every `cardioTick` ms) */
  tick: number
}

/**
 * Returns the next Y value for the rightmost point of the trace.
 * Geometry: Y=MID_Y means flat (no vertical excursion).
 *
 * Each state has its own waveform:
 *  - healthy: full amplitude QRS-like complex
 *  - inspecting: medium-amplitude regular sine (the system is "alive")
 *  - stopping: decaying sine (slowing)
 *  - stopped / cleaning_volumes: flatline
 *  - pulling / recreating: low-amplitude breathing
 *  - awaiting_healthy: ramping sine (recovering rhythm)
 *  - failed: locked flatline
 *  - idle: tiny breath — barely visible
 */
export function nextSample({ state, tick }: NextSampleArgs): number {
  switch (state) {
    case 'idle':
      return MID_Y - 2 * Math.sin(tick * 0.08)

    case 'healthy':
      return MID_Y - qrsComplex(tick, 1.0)

    case 'inspecting':
      return MID_Y - 16 * Math.sin(tick * 0.18)

    case 'stopping':
      // Sine that loses amplitude over the duration of the phase.
      // Tick is local — caller resets on state change for crispness.
      return MID_Y - Math.max(2, 22 - tick * 0.25) * Math.sin(tick * 0.16)

    case 'stopped':
    case 'failed':
      return MID_Y

    case 'cleaning_volumes':
      // Flat with intermittent danger spikes (every ~24 ticks)
      return tick % 24 < 2 ? MID_Y - (tick % 2 === 0 ? 28 : -28) : MID_Y

    case 'pulling':
      // Breathing baseline — tiny up/down so the trace isn't dead
      return MID_Y - 4 * Math.sin(tick * 0.10)

    case 'recreating':
      // Weak, irregular pulse: amplitude varies by tick
      return MID_Y - (8 + 4 * Math.sin(tick * 0.05)) * Math.sin(tick * 0.22)

    case 'awaiting_healthy':
      // Ramp from weak to full QRS over ~80 ticks
      return MID_Y - qrsComplex(tick, Math.min(1, tick / 80))
  }
}

/**
 * QRS-like complex centered at amplitude=1.
 * Simulates: small P wave, sharp R peak, brief S dip, gentle T wave.
 * `intensity` scales the whole shape (0..1).
 */
function qrsComplex(tick: number, intensity: number): number {
  // Repeat every 28 ticks (~1.7Hz at 60ms/tick — close to resting HR)
  const t = tick % 28
  let y = 0
  if (t < 4) y = 4 * Math.sin((t / 4) * Math.PI) // P wave (small bump)
  else if (t < 8) y = 0
  else if (t < 10) y = -8 * (t - 8) // Q dip
  else if (t < 12) y = -16 + 36 * (t - 10) // R peak (sharp up)
  else if (t < 14) y = 56 - 32 * (t - 12) // R falloff
  else if (t < 16) y = -8 // S dip (negative deflection in path coords = below baseline; trace is "MID_Y - y", so y<0 means above)
  else if (t < 22) y = 6 * Math.sin(((t - 16) / 6) * Math.PI) // T wave
  else y = 0
  return y * intensity
}

// ── State derivation from operation ─────────────────────────────────────

const PHASE_TO_STATE: Record<PhaseName, CardiographState> = {
  inspecting: 'inspecting',
  stopping: 'stopping',
  cleaning_volumes: 'cleaning_volumes',
  pulling: 'pulling',
  recreating: 'recreating',
  awaiting_healthy: 'awaiting_healthy',
}

/**
 * Map a DockerOperation row to the cardiograph state. Used by
 * HeroCardiograph to drive the trace from polled data.
 */
export function cardiographStateFromOperation(
  op: DockerOperation | null | undefined,
): CardiographState {
  if (!op) return 'idle'
  if (op.status === 'success') return 'healthy'
  if (op.status === 'failed' || op.status === 'cancelled') return 'failed'
  // running:
  const phase = op.current_phase
  if (phase && phase in PHASE_TO_STATE) return PHASE_TO_STATE[phase]
  return 'idle'
}

// ── Reduced-motion fallback ─────────────────────────────────────────────

/**
 * Returns true when the user prefers reduced motion. SSR-safe (returns
 * false when window is undefined).
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
